import React, { useState, useMemo } from 'react';
import toast from 'react-hot-toast';
import { refreshTokens } from '../services/SimpleTokenService';
import { useDaggerheartData } from '../context/DataContext';
import { useCampaign } from '../context/CampaignContext';
import { hasLoadoutCard } from '../utils/cardFeatureDetection';
import { hasSubclassFeature } from '../utils/featureDetection';

/**
 * DowntimeManager Component
 * Handles core Daggerheart downtime mechanics for non-Beast Feast campaigns.
 * Beast Feast campaigns use "Make a Feast" instead of this system.
 *
 * @param {object} props - The component props.
 * @param {object} props.character - The current character data object.
 * @param {function} props.updateCharacter - Function to update the character state.
 * @param {function} props.onManagerClose - Callback to close the modal.
 */
const DowntimeManager = ({ character, updateCharacter, onManagerClose }) => {
  const allData = useDaggerheartData();
  const { campaignData, updateCharacterInCampaign } = useCampaign();
  const [selectedRestType, setSelectedRestType] = useState('short'); // 'short' or 'long'
  const [selectedActions, setSelectedActions] = useState([]);
  const [projectProgress, setProjectProgress] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  // Check for Clank "Efficient" ancestry feature
  // "When you take a short rest, you can choose a long rest move instead of a short rest move."
  const hasEfficient = character?.ancestryFeatures?.some(f => f.name === 'Efficient');

  // Check for Armorer domain card (VALOR)
  // SRD: "During a rest, when you choose to repair your armor as a downtime move,
  //       your allies also clear an Armor Slot."
  const hasArmorer = useMemo(() => {
    return hasLoadoutCard(character, 'armorer');
  }, [character]);

  // Check for Soothing Speech domain card (GRACE)
  // SRD: "During a short rest, when you take the time to comfort another character
  //       while using the Tend to Wounds downtime move on them, clear an additional
  //       Hit Point on that character. When you do, you also clear 2 Hit Points."
  const hasSoothingSpeech = useMemo(() => {
    return hasLoadoutCard(character, 'soothing-speech');
  }, [character]);

  // State for Soothing Speech ally targeting
  const [soothingSpeechTarget, setSoothingSpeechTarget] = useState(null);

  // Get list of allies for Soothing Speech targeting
  const allies = useMemo(() => {
    if (!campaignData?.characters || !hasSoothingSpeech) return [];
    return Object.entries(campaignData.characters)
      .filter(([id]) => id !== character.id)
      .map(([id, ally]) => ({ id, name: ally.name || 'Unknown' }));
  }, [campaignData?.characters, character.id, hasSoothingSpeech]);

  // Check if party has Martial Preparation (Call of the Slayer mastery)
  // SRD: "Your party gains access to the Martial Preparation downtime move.
  //       Each participant gains a d6 Slayer Die to spend on attack/damage rolls."
  const partyHasMartialPrep = useMemo(() => {
    // Check current character
    if (hasSubclassFeature(character, 'Martial Preparation', 'mastery')) return true;
    // Check party members (if in campaign)
    if (campaignData?.characters) {
      return Object.values(campaignData.characters).some(ally =>
        hasSubclassFeature(ally, 'Martial Preparation', 'mastery')
      );
    }
    return false;
  }, [character, campaignData?.characters]);

  // Short Rest Actions
  const shortRestActions = [
    {
      id: 'tend_wounds',
      name: 'Tend to Wounds',
      description: hasSoothingSpeech && soothingSpeechTarget
        ? `Heal ally +1 HP (Soothing Speech) + heal yourself 2 HP`
        : 'Heal some HP',
      effect: { hp: Math.floor(character.maxHp / 4) } // Heal 1/4 max HP
    },
    {
      id: 'clear_stress',
      name: 'Clear Stress',
      description: 'Reduce stress by 2',
      effect: { stress: -2 }
    },
    {
      id: 'repair_armor',
      name: 'Repair Armor',
      description: hasArmorer ? 'Restore armor points + allies clear an Armor Slot (Armorer)' : 'Restore armor points',
      effect: { armor: 'restore' }
    },
    {
      id: 'prepare',
      name: 'Prepare',
      description: 'Ready yourself for what\'s ahead',
      effect: { prepared: true }
    },
    // Martial Preparation - party downtime from Call of the Slayer mastery
    ...(partyHasMartialPrep ? [{
      id: 'martial_preparation',
      name: '⚔️ Martial Preparation',
      description: 'Train with party. Gain a d6 Slayer Die to spend on attack/damage.',
      effect: { martialPrep: true },
      isPartyMove: true
    }] : [])
  ];

  // Long Rest Exclusive Actions (only available during long rest, or via Efficient)
  const longRestExclusiveActions = [
    {
      id: 'tend_all_wounds',
      name: 'Tend to All Wounds',
      description: 'Fully heal HP',
      effect: { hp: 'full' },
      isLongRestExclusive: true
    },
    {
      id: 'clear_all_stress',
      name: 'Clear All Stress',
      description: 'Remove all stress',
      effect: { stress: 0 },
      isLongRestExclusive: true
    },
    {
      id: 'repair_all_armor',
      name: 'Repair All Armor',
      description: 'Fully restore armor',
      effect: { armor: 'full' },
      isLongRestExclusive: true
    },
    {
      id: 'work_project',
      name: 'Work on a Project',
      description: 'Make progress on a long-term goal',
      effect: { project: true },
      requiresInput: true,
      isLongRestExclusive: true
    }
  ];

  // Long Rest Actions (includes all short rest + long rest exclusive)
  const longRestActions = [
    ...longRestExclusiveActions,
    ...shortRestActions
  ];

  // For short rest with Efficient: show short rest actions + one long rest exclusive option
  // For short rest without Efficient: show only short rest actions
  // For long rest: show all actions
  const availableActions = selectedRestType === 'short'
    ? (hasEfficient ? [...shortRestActions, ...longRestExclusiveActions] : shortRestActions)
    : longRestActions;

  const toggleAction = (actionId) => {
    setSelectedActions(prev => 
      prev.includes(actionId) 
        ? prev.filter(id => id !== actionId)
        : [...prev, actionId]
    );
  };

  const applyDowntime = () => {
    setIsProcessing(true);
    
    let updates = { ...character };
    let resultsLog = [];

    selectedActions.forEach(actionId => {
      const action = availableActions.find(a => a.id === actionId);
      if (!action) return;

      const { effect } = action;

      // Apply HP effects
      if (effect.hp === 'full') {
        updates.hp = updates.maxHp;
        resultsLog.push(`Fully healed to ${updates.maxHp} HP`);
      } else if (typeof effect.hp === 'number') {
        const newHp = Math.min(updates.maxHp, updates.hp + effect.hp);
        resultsLog.push(`Healed ${newHp - updates.hp} HP (${updates.hp} → ${newHp})`);
        updates.hp = newHp;
      }

      // Soothing Speech (GRACE): Bonus healing when using Tend to Wounds on ally
      // SRD: "During a short rest, when you take the time to comfort another character
      //       while using the Tend to Wounds downtime move on them, clear an additional
      //       Hit Point on that character. When you do, you also clear 2 Hit Points."
      if (actionId === 'tend_wounds' && hasSoothingSpeech && soothingSpeechTarget && updateCharacterInCampaign) {
        const targetAlly = campaignData?.characters?.[soothingSpeechTarget];
        if (targetAlly) {
          // Ally clears +1 HP (on top of their own rest healing if they took it)
          const allyMaxHp = targetAlly.maxHp || 8;
          const allyCurrentHp = targetAlly.hp || 0;
          const allyNewHp = Math.min(allyMaxHp, allyCurrentHp + 1);
          if (allyNewHp > allyCurrentHp) {
            updateCharacterInCampaign(soothingSpeechTarget, { hp: allyNewHp });
            resultsLog.push(`🗣️ Soothing Speech: ${targetAlly.name || 'Ally'} cleared 1 HP!`);
          }

          // You also clear 2 HP
          const selfBonusHp = Math.min(updates.maxHp, updates.hp + 2);
          if (selfBonusHp > updates.hp) {
            resultsLog.push(`🗣️ Soothing Speech: You cleared ${selfBonusHp - updates.hp} HP!`);
            updates.hp = selfBonusHp;
          }
        }
      }

      // Apply Stress effects
      if (effect.stress === 0) {
        resultsLog.push(`Cleared all stress (${updates.stress} → 0)`);
        updates.stress = 0;
      } else if (typeof effect.stress === 'number') {
        const newStress = Math.max(0, updates.stress + effect.stress);
        resultsLog.push(`Reduced stress by ${updates.stress - newStress} (${updates.stress} → ${newStress})`);
        updates.stress = newStress;
      }

      // Apply Armor effects
      if (effect.armor === 'full' || effect.armor === 'restore') {
        // Clear one armor mark from the character
        if (updates.armorMarks && updates.armorMarks.length > 0) {
          updates.armorMarks = updates.armorMarks.slice(0, -1); // Remove last mark
          resultsLog.push('Armor repaired (1 Armor Slot cleared)');
        } else {
          resultsLog.push('Armor already at full');
        }

        // Armorer (VALOR): Allies also clear an Armor Slot
        // SRD: "During a rest, when you choose to repair your armor as a downtime move,
        //       your allies also clear an Armor Slot."
        if (hasArmorer && campaignData?.characters && updateCharacterInCampaign) {
          const allyCount = Object.entries(campaignData.characters)
            .filter(([id]) => id !== character.id)
            .reduce((count, [id, ally]) => {
              // Only affect allies that have armor marks
              if (ally.armorMarks && ally.armorMarks.length > 0) {
                const newMarks = ally.armorMarks.slice(0, -1);
                updateCharacterInCampaign(id, { armorMarks: newMarks });
                return count + 1;
              }
              return count;
            }, 0);

          if (allyCount > 0) {
            resultsLog.push(`🛡️ Armorer: ${allyCount} ally/allies cleared an Armor Slot!`);
          } else {
            resultsLog.push('🛡️ Armorer: No allies needed armor repair');
          }
        }
      }

      // Apply preparation effects
      if (effect.prepared) {
        resultsLog.push('You feel prepared and ready');
      }

      // Apply project effects
      if (effect.project && projectProgress.trim()) {
        resultsLog.push(`Made progress on: ${projectProgress}`);
      }

      // Martial Preparation (Call of the Slayer mastery): Gain party Slayer Die
      // SRD: "Your party gains access to the Martial Preparation downtime move.
      //       Each participant gains a d6 Slayer Die to spend on attack/damage rolls."
      if (effect.martialPrep) {
        // Add a d6 Slayer Die to the character's card resources
        if (!updates.cardResources) updates.cardResources = { ...character.cardResources };
        if (!updates.cardResources['party-martial-prep']) {
          updates.cardResources['party-martial-prep'] = {};
        }
        updates.cardResources['party-martial-prep']['slayer-die'] = {
          current: 1,
          maximum: 1,
          dieSize: 6,
          refreshTrigger: 'manual', // Only gained through downtime
          description: 'Slayer Die from Martial Preparation - add to attack/damage roll'
        };
        resultsLog.push('⚔️ Martial Preparation: Gained d6 Slayer Die!');
      }
    });

    // Apply Hope gain for collaborative preparation (if implemented)
    if (selectedActions.includes('prepare') && selectedRestType === 'short') {
      // Could add collaborative Hope bonus logic here
    }

    // Check if Efficient was used (long rest action during short rest)
    const usedEfficient = selectedRestType === 'short' && hasEfficient &&
      selectedActions.some(actionId => longRestExclusiveActions.some(a => a.id === actionId));
    if (usedEfficient) {
      resultsLog.unshift('⚙️ Clank Efficient: Used long rest move during short rest!');
    }

    // Refresh tokens/abilities based on rest type
    const triggerType = selectedRestType === 'long' ? 'long-rest' : 'rest';
    const spellcastTrait = (character.subclass && allData?.subclasses?.[character.subclass]?.spellcastTrait) || null;
    updates = refreshTokens(updates, triggerType, spellcastTrait);

    // Add refresh message to results
    if (selectedRestType === 'long') {
      resultsLog.push('🔄 Long rest abilities refreshed');
    } else {
      resultsLog.push('🔄 Rest abilities refreshed');
    }

    // Show results and update character
    setTimeout(() => {
      updateCharacter(updates);
      setIsProcessing(false);

      // Show success message
      toast.success(`Downtime complete!\n\n${resultsLog.join('\n')}`, { duration: 5000 });
      onManagerClose();
    }, 1000);
  };

  const resetSelections = () => {
    setSelectedActions([]);
    setProjectProgress('');
    setSoothingSpeechTarget(null);
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-xl font-bold text-blue-600 mb-2">🏕️ Downtime</h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Rest and recover between adventures
        </p>
      </div>

      {/* Rest Type Selection */}
      <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
        <h4 className="font-semibold mb-3">Select Rest Type</h4>
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => {
              setSelectedRestType('short');
              resetSelections();
            }}
            className={`p-3 rounded-md text-center font-medium transition-colors ${
              selectedRestType === 'short'
                ? 'bg-blue-600 text-white'
                : 'bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
            }`}
          >
            <div className="font-bold">Short Rest</div>
            <div className="text-xs opacity-75">Quick recovery</div>
          </button>
          <button
            onClick={() => {
              setSelectedRestType('long');
              resetSelections();
            }}
            className={`p-3 rounded-md text-center font-medium transition-colors ${
              selectedRestType === 'long'
                ? 'bg-blue-600 text-white'
                : 'bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
            }`}
          >
            <div className="font-bold">Long Rest</div>
            <div className="text-xs opacity-75">Extended recovery</div>
          </button>
        </div>
      </div>

      {/* Current Character Status */}
      <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
        <h4 className="font-semibold mb-3">Current Status</h4>
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <div className="text-lg font-bold text-red-600">{character.hp}/{character.maxHp}</div>
            <div className="text-xs text-gray-500">Hit Points</div>
          </div>
          <div>
            <div className="text-lg font-bold text-yellow-600">{character.stress}</div>
            <div className="text-xs text-gray-500">Stress</div>
          </div>
          <div>
            <div className="text-lg font-bold text-blue-600">{character.hope}/6</div>
            <div className="text-xs text-gray-500">Hope</div>
          </div>
        </div>
      </div>

      {/* Clank Efficient Feature Banner */}
      {hasEfficient && selectedRestType === 'short' && (
        <div className="bg-cyan-50 dark:bg-cyan-900/20 p-4 rounded-lg border border-cyan-200 dark:border-cyan-800">
          <h4 className="font-semibold text-cyan-800 dark:text-cyan-200 mb-2">
            Clank: Efficient
          </h4>
          <p className="text-sm text-cyan-700 dark:text-cyan-300">
            You can choose a long rest move instead of a short rest move during this short rest.
          </p>
        </div>
      )}

      {/* Armorer Domain Card Banner */}
      {hasArmorer && (
        <div className="bg-amber-50 dark:bg-amber-900/20 p-4 rounded-lg border border-amber-200 dark:border-amber-800">
          <h4 className="font-semibold text-amber-800 dark:text-amber-200 mb-2">
            🛡️ Armorer (VALOR)
          </h4>
          <p className="text-sm text-amber-700 dark:text-amber-300">
            When you repair your armor during a rest, your allies also clear an Armor Slot.
          </p>
        </div>
      )}

      {/* Soothing Speech Domain Card Banner */}
      {hasSoothingSpeech && selectedRestType === 'short' && (
        <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg border border-purple-200 dark:border-purple-800">
          <h4 className="font-semibold text-purple-800 dark:text-purple-200 mb-2">
            🗣️ Soothing Speech (GRACE)
          </h4>
          <p className="text-sm text-purple-700 dark:text-purple-300 mb-3">
            When using Tend to Wounds on an ally, they clear +1 HP and you clear 2 HP.
          </p>
          {selectedActions.includes('tend_wounds') && allies.length > 0 && (
            <div className="mt-2">
              <label className="block text-sm font-medium text-purple-800 dark:text-purple-200 mb-1">
                Select ally to comfort:
              </label>
              <select
                value={soothingSpeechTarget || ''}
                onChange={(e) => setSoothingSpeechTarget(e.target.value || null)}
                className="w-full p-2 text-sm rounded border border-purple-300 dark:border-purple-600 bg-white dark:bg-gray-700"
              >
                <option value="">Self only (no ally bonus)</option>
                {allies.map(ally => (
                  <option key={ally.id} value={ally.id}>{ally.name}</option>
                ))}
              </select>
            </div>
          )}
          {selectedActions.includes('tend_wounds') && allies.length === 0 && (
            <p className="text-xs text-purple-600 dark:text-purple-400 italic">
              No allies in campaign to target. Soothing Speech requires party members.
            </p>
          )}
        </div>
      )}

      {/* Action Selection */}
      <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
        <h4 className="font-semibold mb-3">
          {selectedRestType === 'short' ? 'Short Rest' : 'Long Rest'} Actions
        </h4>
        <div className="space-y-2">
          {availableActions.map(action => (
            <label key={action.id} className={`flex items-start p-3 rounded-md cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-600 ${
              action.isLongRestExclusive && selectedRestType === 'short'
                ? 'bg-cyan-50 dark:bg-cyan-900/30 border border-cyan-200 dark:border-cyan-700'
                : 'bg-white dark:bg-gray-700'
            }`}>
              <input
                type="checkbox"
                checked={selectedActions.includes(action.id)}
                onChange={() => toggleAction(action.id)}
                className="h-4 w-4 mt-0.5 rounded text-blue-600 focus:ring-blue-500"
              />
              <div className="ml-3 flex-1">
                <div className="font-medium flex items-center gap-2">
                  {action.name}
                  {action.isLongRestExclusive && selectedRestType === 'short' && (
                    <span className="text-xs bg-cyan-600 text-white px-2 py-0.5 rounded">
                      Efficient
                    </span>
                  )}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">{action.description}</div>

                {/* Special input for project work */}
                {action.id === 'work_project' && selectedActions.includes('work_project') && (
                  <input
                    type="text"
                    value={projectProgress}
                    onChange={e => setProjectProgress(e.target.value)}
                    placeholder="Describe your project..."
                    className="mt-2 w-full p-2 text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-600"
                  />
                )}
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* Collaborative Actions Info */}
      {selectedRestType === 'short' && selectedActions.includes('prepare') && (
        <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
          <h4 className="font-semibold text-blue-800 dark:text-blue-200 mb-2">
            💡 Collaborative Preparation
          </h4>
          <p className="text-sm text-blue-700 dark:text-blue-300">
            If your party prepares together during a short rest, everyone gains +1 Hope! 
            Coordinate with your GM and party members.
          </p>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex space-x-2">
        <button
          onClick={applyDowntime}
          disabled={selectedActions.length === 0 || isProcessing || (selectedActions.includes('work_project') && !projectProgress.trim())}
          className="flex-1 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed font-medium"
        >
          {isProcessing ? 'Resting...' : `Take ${selectedRestType === 'short' ? 'Short' : 'Long'} Rest`}
        </button>
        <button
          onClick={resetSelections}
          className="px-6 py-3 bg-gray-500 text-white rounded-md hover:bg-gray-600"
        >
          Reset
        </button>
        <button
          onClick={onManagerClose}
          className="px-6 py-3 bg-gray-500 text-white rounded-md hover:bg-gray-600"
        >
          Cancel
        </button>
      </div>

      {/* Feature Refresh Note */}
      <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg border border-yellow-200 dark:border-yellow-800">
        <h4 className="font-semibold text-yellow-800 dark:text-yellow-200 mb-2">
          🔄 Feature Refresh
        </h4>
        <p className="text-sm text-yellow-700 dark:text-yellow-300">
          After a {selectedRestType} rest, remember to refresh your per-rest and per-{selectedRestType}-rest abilities! 
          Check your domain cards and class features.
        </p>
      </div>
    </div>
  );
};

export default DowntimeManager;
